import { Badge, Center, Flex, Text, VStack } from "@chakra-ui/react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { Tooltip } from "../chakra/Tooltip"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "../copy/constants"
import { useCopyValues } from "../providers/helpers/useCopy"
import Link from "@hydrologiq/shared/components/Link"
import { useIsSectionEnabled } from "../providers/SectionFeatures"

const ActivityKindBadge = ({ kind }: { kind: string }) => {
  const colorScheme =
    {
      filling: "blue",
      delivery: "green",
      collection: "orange",
    }[kind] || "gray"

  return (
    <Badge colorScheme={colorScheme} fontSize="xs">
      {kind}
    </Badge>
  )
}

const SiteTypeBadge = ({ type }: { type: string }) => {
  const colorScheme =
    {
      filling: "purple",
      customer: "teal",
      unknown: "gray",
    }[type] || "gray"

  return (
    <Badge colorScheme={colorScheme} variant="subtle" fontSize="xs">
      {type}
    </Badge>
  )
}

const LatestActivity = (props: {
  activity?: gqlTypes.AssetLatestActivity | null
  order?: gqlTypes.CurrentOrder | null
  background?: boolean
  larger?: boolean
  simple?: boolean
}) => {
  const { activity, order, background = true, larger = false, simple = false } = props

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
  })

  if (!activity) {
    return <Text fontSize="sm">No recent activity</Text>
  }

  const site =
    activity.site ||
    (order?.site
      ? { id: order.site.id, name: order.site.name, kind: "customer", customerId: order.customer?.id }
      : null)
  const siteName = site?.name || "Unknown site"
  const siteType = site?.kind || "unknown"
  const activityKind = activity.kind || "unknown"
  const latestEvent = activity.latestEvent?.kind || "scheduled"
  const isCustomersEnabled = useIsSectionEnabled("customers")

  // Determine section link based on site type
  const getSiteLink = () => {
    if (!site) return null
    if (siteType === "filling" && site.fillingSiteId) {
      return `/filling/${site.fillingSiteId}`
    } else if (siteType === "customer" && site.customerId && isCustomersEnabled) {
      return `/customers/${site.customerId}/sites/${site.id}`
    }
    return null
  }

  const siteLink = getSiteLink()

  // Simple mode: just show the site link, no badges or location info
  if (simple) {
    return (
      <Flex align="center" width="100%" minW={0} overflow="hidden">
        {siteLink ? (
          <Link href={siteLink} variant="primary" truncate maxWidth="100%">
            {siteName}
          </Link>
        ) : (
          <Text fontSize="0.75rem" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            {siteName}
          </Text>
        )}
      </Flex>
    )
  }

  const tooltipContent = (
    <VStack align="start" gap={1}>
      <Flex gap={2} align="center">
        <ActivityKindBadge kind={activityKind} />
        <Text fontSize="xs">Activity: {activityKind}</Text>
      </Flex>
      <Flex gap={2} align="center">
        <SiteTypeBadge type={siteType} />
        <Text fontSize="xs">
          {copy[COPY_KEYS.HEADINGS.SITE]}: {siteName}
        </Text>
      </Flex>
      {activity.latestEvent && <Text fontSize="xs">Status: {latestEvent}</Text>}
      {activity.fillLevel && <Text fontSize="xs">Tank level: {activity.fillLevel.value}</Text>}
    </VStack>
  )

  return (
    <Flex
      justifyContent={"space-between"}
      flexDirection={"row"}
      width={larger ? "100%" : 180}
      minHeight={30}
      maxHeight={80}
      borderRadius={5}
      px={background ? 2 : 0}
      mt={larger ? "9px" : 0}
      backgroundColor={background ? "gray.300" : "transparent"}
    >
      <Tooltip content={tooltipContent}>
        <VStack align="start" gap={0} flex={1}>
          <Flex gap={1} align="center">
            <ActivityKindBadge kind={activityKind} />
            <SiteTypeBadge type={siteType} />
          </Flex>
          {siteLink ? (
            <Link href={siteLink} variant="primary">
              {siteName}
            </Link>
          ) : (
            <Text fontSize="xs" lineHeight={"1.2"} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {siteName}
            </Text>
          )}
        </VStack>
      </Tooltip>

      <Center>
        {site?.location ? (
          <Link
            href={`https://www.google.com/maps/place/${site.location.lat},${site.location.long}`}
            rel="noopener noreferrer"
            variant="primary"
          >
            Map
          </Link>
        ) : (
          <Text fontSize="xs" color="gray.500">
            No location
          </Text>
        )}
      </Center>
    </Flex>
  )
}

export default LatestActivity
